import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField, Autocomplete } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { NumberField } from "../../components/NumberField";
import i18n from "../../i18n";
import { ShowAllAutocomplete } from "../../components/ShowAllAutocomplete/ShowAllAutocomplete";
import { LOADING_MACHINE_LABELS } from "../../components/ShowAllAutocomplete/showAllLabels";
import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { GeoCrushingLoaderDTO } from "../../DTOs/Geology/GeoCrushingLoaderDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { MovementLayout, ShiftWindow } from "../GeologyRehandle/movementEntry";
import PlantModelSelect from "../GeologyRehandle/TableCellsComponents/PlantModelSelect";
import TruckSelect from "../GeologyRehandle/TableCellsComponents/TruckSelect";
import ShiftHourSelect from "../GeologyRehandle/TableCellsComponents/ShiftHourSelect";
import MovementTimeSelect from "../GeologyRehandle/TableCellsComponents/MovementTimeSelect";

interface InsertGeoCrushingRowProps {
  state: GeoRehandleDTO;
  setState: React.Dispatch<React.SetStateAction<GeoRehandleDTO>>;
  onAdd: () => Promise<boolean>;
  loaderOptions: GeoCrushingLoaderDTO[];
  isLoaderOptionsLoading: boolean;
  layout: MovementLayout;
  plantModels: GeoRehandleTruckingTruckDTO[];
  trucks: GeoRehandleTruckingTruckDTO[];
  isTruckOptionsLoading: boolean;
  allowOwnLoad: boolean;
  hour?: number;
  hours: string[];
  liveHourIndex: number;
  shiftWindow: ShiftWindow;
  zone: string;
  crushingMovementFromLocationOptions: LocationDTO[];
  isCrushingMovementFromLocationOptionsLoading: boolean;
  crushingMovementToLocationOptions: LocationDTO[];
  isCrushingMovementToLocationOptionsLoading: boolean;
  isReadOnly: boolean
  telemetryEnabled: boolean
}

export const InsertGeoCrushingRow = memo(function InsertRow(
  {
    state,
    setState,
    onAdd,
    loaderOptions,
    isLoaderOptionsLoading,
    layout,
    plantModels,
    trucks,
    isTruckOptionsLoading,
    allowOwnLoad,
    hour,
    hours,
    liveHourIndex,
    shiftWindow,
    zone,
    crushingMovementFromLocationOptions,
    isCrushingMovementFromLocationOptionsLoading,
    crushingMovementToLocationOptions,
    isCrushingMovementToLocationOptionsLoading,
    isReadOnly,
    telemetryEnabled
  }: InsertGeoCrushingRowProps) {
  const { t } = useTranslation("geology");
  return (
    <ul className="py-1 flex gap-2 border-b border-theme-border">
      {layout.timeColumn === "Hour" && <li key="hour" className={layout.time}>
        <ShiftHourSelect
          hour={hour}
          hours={hours}
          liveHourIndex={liveHourIndex}
          disabled={isReadOnly}
          onChange={value => setState(prev => ({ ...prev, hour: value }))}
        />
      </li>}
      {layout.timeColumn === "Time" && <li key="time" className={layout.time}>
        <MovementTimeSelect
          movementTimeUtc={state.movementTimeUtc}
          shiftWindow={shiftWindow}
          zone={zone}
          disabled={isReadOnly}
          onChange={movementTimeUtc => setState(prev => ({ ...prev, movementTimeUtc }))}
        />
      </li>}
      <li key="loader" className={layout.machine}>
        <ShowAllAutocomplete
          labels={LOADING_MACHINE_LABELS}
          classes={{ root: 'w-full' }}
          size="small"
          loading={isLoaderOptionsLoading}
          disabled={isLoaderOptionsLoading || isReadOnly}
          options={loaderOptions}
          getOptionLabel={(option) => option.plantRef}
          value={loaderOptions.find(x => x.operationId === state.operationId) ?? null}
          isOptionEqualToValue={(option, value) => option.operationId === value.operationId}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({
                ...prev,
                plantId: newValue.plantId,
                operationId: newValue.operationId
              }));
            }
          }}
          renderInput={(params) => <TextField
            {...params}
            label={t("movement.loader")}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isLoaderOptionsLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      {layout.carrierColumn === "Plant Model" && <li key="plant-model" className={layout.carrier}>
        <PlantModelSelect
          bucketTypeId={state.bucketTypeId}
          plantModels={plantModels}
          isLoading={isTruckOptionsLoading}
          disabled={isReadOnly}
          onChange={bucketTypeId => setState(prev => ({ ...prev, bucketTypeId }))}
          allowOwnLoad={allowOwnLoad}
          onOwnLoad={() => setState(prev => ({ ...prev, bucketTypeId: undefined }))}
        />
      </li>}
      {layout.carrierColumn === "Truck" && <li key="truck" className={layout.carrier}>
        <TruckSelect
          truckPlantId={state.truckPlantId}
          trucks={trucks}
          isLoading={isTruckOptionsLoading}
          disabled={isReadOnly}
          onChange={truck => setState(prev => ({ ...prev, truckPlantId: truck.plantId, bucketTypeId: truck.bucketTypeId }))}
          allowOwnLoad={allowOwnLoad}
          onOwnLoad={() => setState(prev => ({ ...prev, truckPlantId: undefined, bucketTypeId: undefined }))}
        />
      </li>}
      <li key="movement-from" className={layout.movement}>
        <Autocomplete
          disabled={isReadOnly}
          classes={{ root: 'w-full' }}
          size="small"
          loading={isCrushingMovementFromLocationOptionsLoading}
          options={crushingMovementFromLocationOptions}
          value={crushingMovementFromLocationOptions.find(x => x.id === state.movementFromId) ?? null}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementFromId: newValue.id }));
            }
          }}
          renderInput={(params) => <TextField
            {...params}
            label={t("movement.movementFrom")}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {(isCrushingMovementFromLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      <li key="loads" className="basis-[10%] text-[0.9rem]">
        <NumberField.Root
          disabled={isReadOnly}
          value={state.loads ?? null}
          onValueChange={(val) => {
            if (val === null || val === state.loads) return;
            setState(prev => ({ ...prev, loads: val ?? 0 }));
          }}
          className="h-full bg-theme-surface-2"
        >
          <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
            <NumberField.Decrement />
            <NumberField.Input
              placeholder={i18n.t("fields.loads")}
              className={`h-full w-full px-1 py-2 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition`}
            />
            <NumberField.Increment />
          </NumberField.Group>
        </NumberField.Root>
      </li>
      <li key="movement-to" className={layout.movement}>
        <Autocomplete
          disabled={isReadOnly}
          classes={{ root: 'w-full' }}
          size="small"
          loading={isCrushingMovementToLocationOptionsLoading}
          options={crushingMovementToLocationOptions}
          value={crushingMovementToLocationOptions.find(x => x.id === state.movementToId) ?? null}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementToId: newValue.id }));
            }
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <span className="text-theme-danger-light">{option.name}</span>
              </li>
            )
          }}
          renderInput={(params) => {
            return <TextField
              {...params}
              label={t("movement.movementTo")}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  style: {
                    color: "red",
                    transition: "color 0.2s ease-in-out"
                  },
                  endAdornment: (
                    <>
                      {(isCrushingMovementToLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          }}
        />
      </li>

      {telemetryEnabled && <li key="source" className="basis-[10%]" />}

      <li className="basis-[10%]">
        <button
          disabled={isReadOnly}
          type="button"
          onClick={onAdd}
          className="px-4 py-2 rounded bg-theme-info hover:bg-theme-info-hover cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition duration-300 text-white rounded-sm border-none text-sm font-bold"
        >
          {i18n.t("actions.add")}
        </button>
      </li>
    </ul>
  );
});